import React from 'react';

import styled from 'styled-components';

import getBuilderStateContentVariantData from '~/builder/utils/getBuilderStateContentVariantData';
import { mobile, SkeletonLine } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useGridMeta } from '../context/GridDataContexts';
import useGridInputs, { GRID_TYPES } from '../hooks/useGridInputs';
import useTabsAndBreadcrumbs from '../hooks/useTabsAndBreadcrumbs';

const StyledBreadcrumbsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 350;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.gridV2BreadcrumbsText};

  ${mobile`
    font-size: 12px;
  `}
`;

const StyledBreadcrumbsSeparator = styled.span`
  height: 16px;
  width: 1px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.gridV2BreadcrumbsSeparator};
`;

interface BreadcrumbsProps {
  /** When true, renders only the T2 breadcrumb text (or the fallback search/grid header). */
  compact?: boolean;
}

const Breadcrumbs = ({ compact = false }: BreadcrumbsProps) => {
  const { breadcrumbs, isLoading } = useTabsAndBreadcrumbs();
  const { builderState, gridHeader, gridType } = useGridInputs();
  const { searchQuery: metaSearchQuery } = useGridMeta();

  const searchQuery = metaSearchQuery ?? null;

  const T1breadcrumb = breadcrumbs?.[0];
  const T2breadcrumb = breadcrumbs?.[1];

  // Fall back to the Builder page title when neither real breadcrumbs nor a
  // grid-level header are configured — variant-aware so A/B test variations
  // surface their own title rather than the default content's.
  const pageTitle = getBuilderStateContentVariantData({
    builderStateContent: builderState?.content,
  })?.title;
  const fallbackHeader = gridHeader || pageTitle;

  if (isLoading) {
    return <SkeletonLine width={150} height="auto" />;
  }

  if (gridType === GRID_TYPES.CONSTRUCTOR_SEARCH_GRID) {
    return (
      <StyledBreadcrumbsContainer>
        {!compact && (
          <>
            <FormattedMessage id="grid_static.search" defaultMessage="Search" />
            <StyledBreadcrumbsSeparator />
          </>
        )}
        <p>"{searchQuery}"</p>
      </StyledBreadcrumbsContainer>
    );
  }

  if (!T1breadcrumb || !T2breadcrumb) {
    return (
      <StyledBreadcrumbsContainer>
        <p>{fallbackHeader}</p>
      </StyledBreadcrumbsContainer>
    );
  }

  if (compact) {
    const lowestBreadcrumb = breadcrumbs?.[breadcrumbs.length - 1];
    return (
      <StyledBreadcrumbsContainer>
        <p>{lowestBreadcrumb}</p>
      </StyledBreadcrumbsContainer>
    );
  }

  return (
    <StyledBreadcrumbsContainer>
      <p>{T1breadcrumb}</p>
      <StyledBreadcrumbsSeparator />
      <p>{T2breadcrumb}</p>
    </StyledBreadcrumbsContainer>
  );
};

// Memoize so products-driven re-renders in the parent grid view don't cascade
// into this component. Breadcrumbs only subscribes to GridMetaContext.
export default React.memo(Breadcrumbs);
